import React from "react";
import { styled } from "@glitz/react";
import {
  black,
  darkGrey,
  fontSize,
  fontWeight,
  green,
  grey,
  mediumGrey,
  spacing,
  white,
} from "../../theme";

export type SegmentedOption<T extends string = string> = {
  id: T;
  label: React.ReactNode;
  disabled?: boolean;
};

export type SegmentedControlSize = "sm" | "md";

type Props<T extends string = string> = {
  /** Valfri label som renderas ovanför knapparna. */
  label?: string;
  /** Valfri hjälptext under label. */
  hint?: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: SegmentedControlSize;
  /** aria-label om ingen synlig label används. */
  ariaLabel?: string;
};

function SegmentedControl<T extends string = string>({
  label,
  hint,
  options,
  value,
  onChange,
  size = "md",
  ariaLabel,
}: Props<T>) {
  return (
    <Wrap>
      {label ? <Label>{label}</Label> : null}
      {hint ? <Hint>{hint}</Hint> : null}
      <Row role="radiogroup" aria-label={ariaLabel ?? label}>
        {options.map((opt) => {
          const active = opt.id === value;
          return (
            <Chip
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={opt.disabled}
              css={{
                ...(size === "sm" ? chipSizeSm : chipSizeMd),
                ...(active ? chipActive : chipInactive),
                ...(opt.disabled ? chipDisabled : null),
              }}
              onClick={() => {
                if (opt.disabled || active) return;
                onChange(opt.id);
              }}
            >
              {opt.label}
            </Chip>
          );
        })}
      </Row>
    </Wrap>
  );
}

export default SegmentedControl;

const Wrap = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
  width: "100%",
});

const Label = styled.span({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: darkGrey,
});

const Hint = styled.span({
  fontSize: fontSize.xxs,
  color: darkGrey,
  lineHeight: "1.45",
  marginTop: `-${spacing.xxs}`,
});

const Row = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
});

const Chip = styled.button({
  cursor: "pointer",
  fontWeight: fontWeight.bold,
  borderRadius: "8px",
  transitionProperty: "background-color, border-color, color",
  transitionDuration: "0.15s",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.xxs,
});

const chipSizeMd = {
  fontSize: fontSize.s,
  padding: { x: spacing.m, y: spacing.xs },
  border: { xy: { style: "solid" as const, width: "2px" } },
};

const chipSizeSm = {
  fontSize: fontSize.xs,
  padding: { x: spacing.s, y: "6px" },
  border: { xy: { style: "solid" as const, width: "1px" } },
};

const chipInactive = {
  backgroundColor: white,
  color: black,
  border: { xy: { style: "solid" as const, color: grey, width: "2px" } },
  ":hover": {
    border: { xy: { style: "solid" as const, color: black, width: "2px" } },
  },
};

const chipActive = {
  backgroundColor: black,
  color: white,
  border: { xy: { style: "solid" as const, color: black, width: "2px" } },
  ":hover": {
    backgroundColor: green,
    border: { xy: { style: "solid" as const, color: green, width: "2px" } },
  },
};

const chipDisabled = {
  cursor: "not-allowed",
  opacity: 0.45,
  backgroundColor: mediumGrey,
  color: darkGrey,
  border: { xy: { style: "solid" as const, color: grey, width: "2px" } },
  ":hover": {
    backgroundColor: mediumGrey,
    border: { xy: { style: "solid" as const, color: grey, width: "2px" } },
  },
};
